import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { verifyPin } from '../services/Users/userService';
import useUsernameFromToken from '../hooks/useUsernameFromToken';
import { tokenStorage, userDataStorage, usernameStorage, pinVerifiedStorage, clearAllAuthData } from '../utils/encryptedCookie';

const PinVerificationContext = createContext();

const INACTIVITY_TIMEOUT = 5 * 60 * 1000;
const MAX_ATTEMPTS = 3;

export const PinVerificationProvider = ({ children }) => {
  const [showPinModal, setShowPinModal] = useState(false);
  const [showActionPinModal, setShowActionPinModal] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [actionAttempts, setActionAttempts] = useState(0);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [userHasPin, setUserHasPin] = useState(false);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const attemptsRef = useRef(0);
  const actionAttemptsRef = useRef(0);
  const actionPinCallbackRef = useRef(null);
  const actionPinValueRef = useRef(null);
  const modalOpenedRef = useRef(false);
  const { getUsernameFromToken } = useUsernameFromToken();

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (userHasPin && isPinVerified) {
      inactivityTimerRef.current = setTimeout(() => {
        setIsPinVerified(false);
        pinVerifiedStorage.remove();
        setShowPinModal(true);
        setAttempts(0);
        attemptsRef.current = 0;
        modalOpenedRef.current = false;
      }, INACTIVITY_TIMEOUT);
    }
  }, [userHasPin, isPinVerified]);

  const checkPinRequired = useCallback(async () => {
    const token = tokenStorage.get();
    if (!token) {
      setUserHasPin(false);
      setIsPinVerified(false);
      pinVerifiedStorage.remove();
      return false;
    }

    const username = getUsernameFromToken();
    if (!username) {
      return false;
    }

    let userData = userDataStorage.get();

    if (!userData) {
      try {
        const { getUserData } = await import('../services/Users/userService');
        userData = await getUserData(username);
        userDataStorage.set(userData);
        usernameStorage.set(username);
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        return false;
      }
    }

    const hasPin = userData.isPin === true;
    setUserHasPin(hasPin);
    
    if (hasPin) {
      const pinVerifiedLocal = pinVerifiedStorage.get();
      const pinVerifiedSession = sessionStorage.getItem(`pinVerified_${username}`) === 'true';
      const pinVerified = pinVerifiedLocal && pinVerifiedSession;
      
      if (!pinVerified) {
        setIsPinVerified(false);
        if (!modalOpenedRef.current) {
          setAttempts(0);
          attemptsRef.current = 0;
          modalOpenedRef.current = true;
        }
        setShowPinModal(true);
        return true;
      } else {
        setIsPinVerified(true);
        setShowPinModal(false);
        lastActivityRef.current = Date.now();
        resetInactivityTimer();
      }
    } else {
      setIsPinVerified(true);
      pinVerifiedStorage.set(true);
      sessionStorage.setItem(`pinVerified_${username}`, 'true');
      setShowPinModal(false);
    }
    
    return false;
  }, [getUsernameFromToken, resetInactivityTimer]);

  const handlePinVerify = useCallback(async (pin) => {
    const username = getUsernameFromToken();
    if (!username) {
      throw new Error('Usuário não encontrado');
    }

    try {
      const result = await verifyPin(username, pin);
      
      if (result && (result.isValid === true || result.isValid === 'true')) {
        setAttempts(0);
        attemptsRef.current = 0;
        modalOpenedRef.current = false;
        setIsPinVerified(true);
        pinVerifiedStorage.set(true);
        sessionStorage.setItem(`pinVerified_${username}`, 'true');
        lastActivityRef.current = Date.now();
        setShowPinModal(false);
        resetInactivityTimer();
        return true;
      } else {
        throw new Error('PIN incorreto.');
      }
    } catch (error) {
      const errorMessage = error.message || 'PIN incorreto.';
      
      attemptsRef.current = attemptsRef.current + 1;
      const newAttempts = attemptsRef.current;
      
      setAttempts(newAttempts);
      
      if (newAttempts >= MAX_ATTEMPTS) {
        setAttempts(MAX_ATTEMPTS);
        setTimeout(() => {
          const currentUsername = getUsernameFromToken();
          clearAllAuthData();
          if (currentUsername) {
            sessionStorage.removeItem(`pinVerified_${currentUsername}`);
          }
          sessionStorage.clear();
          window.location.href = '/login';
        }, 2000);
        throw new Error('Máximo de tentativas excedido. Você será desconectado.');
      }
      
      throw new Error(errorMessage);
    }
  }, [getUsernameFromToken, resetInactivityTimer]);

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (userHasPin && isPinVerified) {
      resetInactivityTimer();
    }
  }, [userHasPin, isPinVerified, resetInactivityTimer]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
    };
  }, [handleActivity]);

  useEffect(() => {
    checkPinRequired();
  }, [checkPinRequired]);

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  const setUserData = useCallback((data) => {
    if (data) {
      const username = getUsernameFromToken();
      userDataStorage.set(data);
      if (username) {
        usernameStorage.set(username);
      }
      const hasPin = data.isPin === true;
      setUserHasPin(hasPin);
      
      if (hasPin) {
        pinVerifiedStorage.remove();
        if (username) {
          sessionStorage.removeItem(`pinVerified_${username}`);
        }
        setIsPinVerified(false);
        setShowPinModal(true);
        setAttempts(0);
        attemptsRef.current = 0;
        modalOpenedRef.current = false;
      } else {
        setIsPinVerified(true);
        pinVerifiedStorage.set(true);
        if (username) {
          sessionStorage.setItem(`pinVerified_${username}`, 'true');
        }
      }
    }
  }, [getUsernameFromToken]);

  const clearPinVerification = useCallback(() => {
    const username = getUsernameFromToken();
    setIsPinVerified(false);
    setShowPinModal(false);
    setAttempts(0);
    attemptsRef.current = 0;
    pinVerifiedStorage.remove();
    userDataStorage.remove();
    usernameStorage.remove();
    if (username) {
      sessionStorage.removeItem(`pinVerified_${username}`);
    }
  }, [getUsernameFromToken]);

  const handleActionPinVerify = useCallback(async (pin) => {
    const username = getUsernameFromToken();
    if (!username) {
      throw new Error('Usuário não encontrado');
    }

    try {
      const result = await verifyPin(username, pin);
      
      if (result && (result.isValid === true || result.isValid === 'true')) {
        setActionAttempts(0);
        actionAttemptsRef.current = 0;
        setShowActionPinModal(false);
        actionPinValueRef.current = pin;
        
        if (actionPinCallbackRef.current) {
          actionPinCallbackRef.current.resolve(pin);
          actionPinCallbackRef.current = null;
        }
        return true;
      } else {
        throw new Error('PIN incorreto.');
      }
    } catch (error) {
      const errorMessage = error.message || 'PIN incorreto.';
      
      actionAttemptsRef.current = actionAttemptsRef.current + 1;
      const newAttempts = actionAttemptsRef.current;
      
      setActionAttempts(newAttempts);
      
      if (newAttempts >= MAX_ATTEMPTS) {
        setActionAttempts(MAX_ATTEMPTS);
        setShowActionPinModal(false);
        if (actionPinCallbackRef.current) {
          actionPinCallbackRef.current.reject(new Error('Máximo de tentativas excedido.'));
          actionPinCallbackRef.current = null;
        }
        throw new Error('Máximo de tentativas excedido.');
      }
      
      throw new Error(errorMessage);
    }
  }, [getUsernameFromToken]);

  const requirePinVerification = useCallback(() => {
    return new Promise((resolve, reject) => {
      const username = getUsernameFromToken();
      if (!username) {
        reject(new Error('Usuário não encontrado'));
        return;
      }

      const userData = userDataStorage.get();

      if (!userData) {
        reject(new Error('Dados do usuário não encontrados'));
        return;
      }

      const hasPin = userData.isPin === true;
      
      if (!hasPin) {
        resolve();
        return;
      }

      actionPinCallbackRef.current = { resolve, reject };
      actionPinValueRef.current = null;
      setActionAttempts(0);
      actionAttemptsRef.current = 0;
      setShowActionPinModal(true);
    });
  }, [getUsernameFromToken]);

  const requirePinForUpdate = useCallback(() => {
    return new Promise((resolve, reject) => {
      const username = getUsernameFromToken();
      if (!username) {
        reject(new Error('Usuário não encontrado'));
        return;
      }

      const userData = userDataStorage.get();

      if (!userData) {
        reject(new Error('Dados do usuário não encontrados'));
        return;
      }

      const hasPin = userData.isPin === true;
      
      if (!hasPin) {
        resolve(null);
        return;
      }

      actionPinCallbackRef.current = { resolve, reject };
      actionPinValueRef.current = null;
      setActionAttempts(0);
      actionAttemptsRef.current = 0;
      setShowActionPinModal(true);
    });
  }, [getUsernameFromToken]);

  const closeActionPinModal = useCallback(() => {
    setShowActionPinModal(false);
    if (actionPinCallbackRef.current) {
      actionPinCallbackRef.current.reject(new Error('Verificação de PIN cancelada.'));
      actionPinCallbackRef.current = null;
    }
    setActionAttempts(0);
    actionAttemptsRef.current = 0;
  }, []);

  return (
    <PinVerificationContext.Provider
      value={{
        showPinModal,
        showActionPinModal,
        attempts,
        actionAttempts,
        isPinVerified,
        userHasPin,
        handlePinVerify,
        handleActionPinVerify,
        requirePinVerification,
        requirePinForUpdate,
        closeActionPinModal,
        setUserData,
        clearPinVerification,
        checkPinRequired,
      }}
    >
      {children}
    </PinVerificationContext.Provider>
  );
};

export const usePinVerification = () => {
  const context = useContext(PinVerificationContext);
  if (!context) {
    throw new Error('usePinVerification deve ser usado dentro de PinVerificationProvider');
  }
  return context;
};
